import { useState, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, CreditCard, Plus, Trash2, ChevronDown, ChevronRight, Layers, Link2, X, Compass } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import DynamicCardFields from "@/components/activities/DynamicCardFields";
import type {
  EditorCardStep,
  EditorCardGroupStep,
  TemplateContentFields,
  CardTooltips,
} from "@/types/activity";

/* ─── Sortable card item ─────────────────────────────────── */

function SortableCardItem({
  card,
  index,
  isExpanded,
  onToggle,
  onRemove,
  onUpdate,
  fields,
  personalizePaths,
  publishedPaths,
}: {
  card: EditorCardStep;
  index: number;
  isExpanded: boolean;
  onToggle: () => void;
  onRemove: () => void;
  onUpdate: (updates: Partial<EditorCardStep>) => void;
  fields: TemplateContentFields | null;
  personalizePaths?: boolean;
  publishedPaths?: { id: string; title: string }[];
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: card.localId,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : "auto",
  };

  return (
    <div
      ref={setNodeRef}
      style={style as any}
      className="rounded-lg border-2 border-group-pale bg-card overflow-hidden"
    >
      <div className="flex items-center gap-2 px-3 py-2.5">
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground touch-none"
        >
          <GripVertical className="h-4 w-4" />
        </button>
        <button onClick={onToggle} className="flex items-center gap-2 flex-1 min-w-0 text-left">
          {isExpanded ? (
            <ChevronDown className="h-4 w-4 text-group shrink-0" />
          ) : (
            <ChevronRight className="h-4 w-4 text-group shrink-0" />
          )}
          <CreditCard className="h-4 w-4 text-group shrink-0" />
          <span className="text-sm font-medium truncate">
            {card.templateLabel || "Carte"}
            {(() => {
              const title = Object.values(card.content).find(
                (v) => typeof v === "string" && v.trim()
              );
              return title ? ` | ${String(title).trim().length > 25 ? String(title).trim().slice(0, 22) + "…" : String(title).trim()}` : "";
            })()}
          </span>
          <Badge variant="outline" className="text-[10px] border-group-pale text-group shrink-0 ml-auto">
            #{index + 1}
          </Badge>
        </button>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-destructive shrink-0"
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
      {isExpanded && fields?.fields && (
        <div className="px-3 pb-3 border-t border-group-pale/50">
          <div className="pt-3 space-y-4">
            <DynamicCardFields
              fields={fields.fields}
              values={card.content}
              onChange={(content) => onUpdate({ content })}
              templateCode={card.templateCode}
            />
            {/* Tooltips section – same UI as StepConfigPanel */}
            <TooltipsEditor
              tooltips={card.tooltips ?? null}
              onChange={(tooltips) => onUpdate({ tooltips })}
            />
            {/* Path personalization for single_choice in personalize_paths groups */}
            {personalizePaths && card.templateCode === "single_choice" && publishedPaths && (
              <PathPersonalizationEditor
                card={card}
                publishedPaths={publishedPaths}
                onUpdate={onUpdate}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Path Personalization Editor ────────────────────────── */

function PathPersonalizationEditor({
  card,
  publishedPaths,
  onUpdate,
}: {
  card: EditorCardStep;
  publishedPaths: { id: string; title: string }[];
  onUpdate: (updates: Partial<EditorCardStep>) => void;
}) {
  const perso = (card.content as any)?.path_personalization as {
    path_ids?: string[];
    option_effects?: Record<string, string>;
  } | undefined;

  const selectedPathIds = perso?.path_ids || [];
  const optionEffects = perso?.option_effects || {};

  // Extract options from content
  const options = ((card.content as any)?.options || []) as { id: string; label?: string; value?: string }[];

  const updatePerso = (patch: Partial<{ path_ids: string[]; option_effects: Record<string, string> }>) => {
    const current = perso || { path_ids: [], option_effects: {} };
    const updated = { ...current, ...patch };
    onUpdate({
      content: { ...card.content, path_personalization: updated },
    });
  };

  const togglePath = (pathId: string) => {
    const next = selectedPathIds.includes(pathId)
      ? selectedPathIds.filter((id) => id !== pathId)
      : [...selectedPathIds, pathId];
    updatePerso({ path_ids: next });
  };

  return (
    <div className="space-y-3 rounded-md border border-amber-200 bg-amber-50/50 p-3">
      <div className="flex items-center gap-2">
        <Compass className="h-4 w-4 text-amber-600" />
        <Label className="text-sm font-medium text-amber-800">Personnalisation des parcours</Label>
      </div>

      {/* Path selection */}
      <div className="space-y-1.5">
        <Label className="text-xs text-muted-foreground">Parcours impactés</Label>
        <div className="max-h-32 overflow-y-auto space-y-1 rounded border border-border p-2 bg-background">
          {publishedPaths.length === 0 ? (
            <p className="text-xs text-muted-foreground">Aucun parcours publié disponible</p>
          ) : (
            publishedPaths.map((p) => (
              <label key={p.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-accent/50 rounded px-1 py-0.5">
                <Checkbox
                  checked={selectedPathIds.includes(p.id)}
                  onCheckedChange={() => togglePath(p.id)}
                />
                <span className="truncate">{p.title}</span>
              </label>
            ))
          )}
        </div>
      </div>

      {/* Option effects */}
      {options.length > 0 && selectedPathIds.length > 0 && (
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">Effet par option</Label>
          <div className="space-y-1">
            {options.map((opt) => (
              <div key={opt.id} className="flex items-center gap-2">
                <span className="text-sm flex-1 truncate">{opt.label || opt.value || opt.id}</span>
                <Select
                  value={optionEffects[opt.id] || "__none__"}
                  onValueChange={(v) => {
                    const next = { ...optionEffects };
                    if (v === "__none__") {
                      delete next[opt.id];
                    } else {
                      next[opt.id] = v;
                    }
                    updatePerso({ option_effects: next });
                  }}
                >
                  <SelectTrigger className="w-[130px] h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Aucun effet</SelectItem>
                    <SelectItem value="add">+1 point</SelectItem>
                    <SelectItem value="remove">-1 point</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


interface CardGroupEditorDialogProps {
  open: boolean;
  onClose: () => void;
  step: EditorCardGroupStep;
  templates: { id: string; code: string; label: string; is_interactive?: boolean }[];
  getTemplateFields: (templateId: string) => TemplateContentFields | null;
  onUpdateStep: (localId: string, updates: Partial<EditorCardGroupStep>) => void;
  onAddCard: (stepLocalId: string, templateId: string) => void;
  onRemoveCard: (stepLocalId: string, cardIdx: number) => void;
  onUpdateCard: (stepLocalId: string, cardIdx: number, updates: Partial<EditorCardStep>) => void;
  pathType?: string | null;
  readOnly?: boolean;
}

/* ─── Tooltips editor (shared with StepConfigPanel) ──────── */

function TooltipsEditor({
  tooltips,
  onChange,
}: {
  tooltips: CardTooltips | null;
  onChange: (t: CardTooltips | null) => void;
}) {
  if (tooltips) {
    return (
      <div className="space-y-2 rounded-md border border-border p-3">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-medium">Infobulle – Sources</Label>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-destructive"
            onClick={() => onChange(null)}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
        <Input
          value={tooltips.title || ""}
          onChange={(e) =>
            onChange({ ...tooltips, title: e.target.value || undefined, items: tooltips.items || [] })
          }
          placeholder="Titre (ex: Sources scientifiques)"
        />
        {(tooltips.items || []).map((item, idx) => (
          <div key={idx} className="flex gap-2 items-start">
            <div className="flex-1 space-y-1">
              <Input
                value={item.description}
                onChange={(e) => {
                  const newItems = [...(tooltips.items || [])];
                  newItems[idx] = { ...newItems[idx], description: e.target.value };
                  onChange({ ...tooltips, items: newItems });
                }}
                placeholder="Description"
              />
              <Input
                value={item.url}
                onChange={(e) => {
                  const newItems = [...(tooltips.items || [])];
                  newItems[idx] = { ...newItems[idx], url: e.target.value };
                  onChange({ ...tooltips, items: newItems });
                }}
                placeholder="https://…"
              />
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="shrink-0 mt-0.5"
              onClick={() => {
                const newItems = (tooltips.items || []).filter((_, i) => i !== idx);
                onChange({ ...tooltips, items: newItems });
              }}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ))}
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            const newItems = [...(tooltips.items || []), { url: "", description: "" }];
            onChange({ ...tooltips, items: newItems });
          }}
        >
          + Ajouter un lien
        </Button>
      </div>
    );
  }

  return (
    <Button
      variant="outline"
      size="sm"
      className="w-full"
      onClick={() => onChange({ title: "Sources scientifiques", items: [] })}
    >
      <Link2 className="h-4 w-4 mr-1" />
      Préciser des sources en infobulle
    </Button>
  );
}

export default function CardGroupEditorDialog({
  open,
  onClose,
  step,
  templates,
  getTemplateFields,
  onUpdateStep,
  onAddCard,
  onRemoveCard,
  onUpdateCard,
  pathType,
  readOnly,
}: CardGroupEditorDialogProps) {
  const [expandedCardId, setExpandedCardId] = useState<string | null>(null);
  const [addCardTemplateId, setAddCardTemplateId] = useState("");

  const personalizePaths = !!(step.config as any)?.personalize_paths;

  // Load published paths of type 'path' for personalization
  const { data: publishedPaths } = useQuery({
    queryKey: ["published-paths-for-personalization"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("path")
        .select("id, title, path_root_id, version")
        .eq("is_published", true)
        .eq("path_type", "path")
        .order("title");
      if (error) throw error;
      // Keep latest version per root
      const latestByRoot = new Map<string, { id: string; title: string; version: number }>();
      for (const p of data || []) {
        const key = p.path_root_id || p.id;
        const existing = latestByRoot.get(key);
        if (!existing || p.version > existing.version) {
          latestByRoot.set(key, { id: p.id, title: p.title, version: p.version });
        }
      }
      return Array.from(latestByRoot.values()).map((p) => ({ id: p.id, title: p.title }));
    },
    enabled: pathType === "onboarding" && personalizePaths,
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = step.cards.findIndex((c) => c.localId === active.id);
      const newIndex = step.cards.findIndex((c) => c.localId === over.id);
      if (oldIndex === -1 || newIndex === -1) return;
      const reordered = arrayMove([...step.cards], oldIndex, newIndex);
      onUpdateStep(step.localId, { cards: reordered } as any);
    },
    [step, onUpdateStep],
  );

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col overflow-hidden">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Layers className="h-5 w-5 text-group" />
            <DialogTitle>{step.title || "Groupe"}</DialogTitle>
            <Badge className="bg-group/10 text-group border-group-pale text-xs">
              {step.cards.length} carte(s)
            </Badge>
          </div>
          <DialogDescription>
            Gérez les cartes du groupe. Glissez-déposez pour réordonner.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 px-1">
          <div className="space-y-1.5">
            <Label className="text-sm">Titre du groupe</Label>
            <Input
              value={step.title}
              onChange={(e) => onUpdateStep(step.localId, { title: e.target.value } as any)}
              placeholder="Nom du groupe"
              className="border-group-pale focus-visible:ring-group"
              disabled={readOnly}
            />
          </div>

          {(pathType === "meteo" || pathType === "onboarding") && (
            <div className="space-y-1.5">
              <Label className="text-sm">Type de score météo</Label>
            <Select
              value={(step.config as any)?.meteo_score_type || "__none__"}
              disabled={readOnly}
                onValueChange={(v) => {
                  const newConfig = { ...(step.config as Record<string, unknown> || {}) };
                  if (v === "__none__") {
                    delete newConfig.meteo_score_type;
                  } else {
                    newConfig.meteo_score_type = v;
                  }
                  onUpdateStep(step.localId, { config: Object.keys(newConfig).length > 0 ? newConfig : null } as any);
                }}
              >
                <SelectTrigger className="border-group-pale">
                  <SelectValue placeholder="Non comptabilisé" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Non comptabilisé</SelectItem>
                  <SelectItem value="wellbeing">Bien-être</SelectItem>
                  <SelectItem value="suffering">Souffrance</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {pathType === "onboarding" && (
            <div className="flex items-center justify-between rounded-md border border-border p-3">
              <div className="space-y-0.5">
                <Label className="text-sm font-medium flex items-center gap-1.5">
                  <Compass className="h-4 w-4" />
                  Personnaliser les parcours
                </Label>
                <p className="text-xs text-muted-foreground">
                  Les cartes single_choice de ce groupe influenceront l'ordre des parcours
                </p>
              </div>
              <Switch
                checked={personalizePaths}
                disabled={readOnly}
                onCheckedChange={(checked) => {
                  const newConfig = { ...(step.config as Record<string, unknown> || {}) };
                  if (checked) {
                    newConfig.personalize_paths = true;
                  } else {
                    delete newConfig.personalize_paths;
                  }
                  onUpdateStep(step.localId, { config: Object.keys(newConfig).length > 0 ? newConfig : null } as any);
                }}
              />
            </div>
          )}
        </div>

        {/* Cards list */}
        <div className="flex-1 min-h-0 overflow-y-auto px-1">
          <div className="space-y-2 py-2">
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={step.cards.map((c) => c.localId)} strategy={verticalListSortingStrategy}>
                {step.cards.map((card, idx) => (
                  <SortableCardItem
                    key={card.localId}
                    card={card}
                    index={idx}
                    isExpanded={expandedCardId === card.localId}
                    onToggle={() => setExpandedCardId(expandedCardId === card.localId ? null : card.localId)}
                    onRemove={() => !readOnly && onRemoveCard(step.localId, idx)}
                    onUpdate={(updates) => !readOnly && onUpdateCard(step.localId, idx, updates)}
                    fields={getTemplateFields(card.templateId)}
                    personalizePaths={personalizePaths}
                    publishedPaths={publishedPaths || []}
                  />
                ))}
              </SortableContext>
            </DndContext>

            {step.cards.length === 0 && (
              <div className="text-center py-8 text-muted-foreground text-sm">
                Aucune carte dans ce groupe. Ajoutez-en une ci-dessous.
              </div>
            )}
          </div>
        </div>

        {/* Add card */}
        {!readOnly && (
        <div className="flex items-center gap-2 pt-2 border-t border-border px-1">
          <Select value={addCardTemplateId} onValueChange={setAddCardTemplateId}>
            <SelectTrigger className="flex-1">
              <SelectValue placeholder="Choisir un template…" />
            </SelectTrigger>
            <SelectContent>
              {templates.filter((t) => t.is_interactive).length > 0 && (
                <SelectGroup>
                  <SelectLabel>Interactives</SelectLabel>
                  {templates.filter((t) => t.is_interactive).map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
                  ))}
                </SelectGroup>
              )}
              {templates.filter((t) => !t.is_interactive).length > 0 && (
                <SelectGroup>
                  <SelectLabel>Non interactives</SelectLabel>
                  {templates.filter((t) => !t.is_interactive).map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
                  ))}
                </SelectGroup>
              )}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            disabled={!addCardTemplateId}
            className="border-group-pale text-group hover:bg-group/10"
            onClick={() => {
              onAddCard(step.localId, addCardTemplateId);
              setAddCardTemplateId("");
            }}
          >
            <Plus className="h-4 w-4 mr-1" />
            Ajouter
          </Button>
        </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
